import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Schemas } from '@npc/api-client'

import { api } from '../../../app/api'
import { call } from '../../../app/errors'
import { hhmm, monthDayTime } from '../../../app/format'
import { Modal } from '../../../components/modal'
import { t } from '../../../copy'
import { T } from '../../../copy/text'
import { PJ } from '../../../copy/pjshell-text'
import type { PjshellId } from '../../../copy/pjshell'
import { certificateUploadFiles } from './certificate-files'
import {
  useConfigureProjectEnvironment,
  useProject,
  useProjectEnvironments,
  useProjectResources,
} from '../../../app/queries'
import { useProjectId } from './params'
import {
  DataCell,
  DataRow,
  DataTable,
  GroupLabel,
  KvRows,
  ModuleError,
  ModuleHeader,
  ModulePage,
  ModulePanel,
  ModuleSkeleton,
  StatusPill,
  Uncollected,
  Unverified,
  luluButtonClass,
  luluPrimaryButtonClass,
  moduleTitle,
} from './ui'

type EnvKind = 'test' | 'production'

export function environmentOf(
  data: { items?: Schemas['ProjectEnvironment'][] | null } | undefined,
  kind: EnvKind,
): Schemas['ProjectEnvironment'] | null {
  if (!Array.isArray(data?.items)) return null
  return data.items.find((item) => item.kind === kind) ?? null
}

export function environmentStatusCopy(env: Schemas['ProjectEnvironment'] | null): PjshellId | 'unverified' {
  if (!env) return 'pjshell.env.no_bundle'
  if (env.status === 'ready') return 'pjshell.env.status.ready'
  if (env.status === 'failed') return 'pjshell.env.status.failed'
  if (env.status === 'rolled_back') return 'pjshell.env.status.rolled_back'
  if (env.status === 'unverified' && !env.active_release) return 'pjshell.env.no_bundle'
  return 'unverified'
}

function canPromote(test: Schemas['ProjectEnvironment'] | null): boolean {
  return test?.status === 'ready' && Boolean(test.active_release)
}

export function ProjectEnvironmentsPage() {
  const projectId = useProjectId()
  if (!projectId) {
    return (
      <ModulePage testid="project-module-environments">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  return <Loaded projectId={projectId} />
}

export { ProjectEnvironmentsPage as ProjectEnvironments }

function Loaded({ projectId }: { projectId: string }) {
  const project = useProject(projectId)
  const queryClient = useQueryClient()
  const envs = useProjectEnvironments(projectId)
  const resources = useProjectResources(projectId)
  const configure = useConfigureProjectEnvironment(projectId)
  const test = environmentOf(envs.data, 'test')
  const production = environmentOf(envs.data, 'production')
  const persistedConfiguration = production?.configuration ?? test?.configuration ?? null
  const promoteReady = canPromote(test)
  const productionRelease = production?.active_release
  const canRollback = Boolean(productionRelease?.previous_release_id)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [configureOpen, setConfigureOpen] = useState(false)
  const [domain, setDomain] = useState('')
  const [environmentKind, setEnvironmentKind] = useState<EnvKind>('production')
  const [hostId, setHostId] = useState('')
  const [certificateFiles, setCertificateFiles] = useState<File[]>([])
  const [configuration, setConfiguration] = useState<Schemas['EnvironmentConfiguration'] | null>(null)
  const assignedHosts = resources.data?.hosts.filter(
    (host) => host.occupancy === 'exclusive' || host.occupancy === 'shared',
  ) ?? []
  const testHost = assignedHosts.find((host) => host.id === test?.configuration?.host_id)
    ?? assignedHosts.find((host) => host.environment_kind === 'test')
  const testPublicUrl = test?.configuration?.domain
    ? `${test.configuration.certificate_status === 'stored' ? 'https' : 'http'}://${test.configuration.domain}`
    : testHost?.public_address ? machineAddressHref(testHost.address) : null
  const selectedHostId = hostId || assignedHosts.find((host) => host.environment_kind === environmentKind)?.id || assignedHosts[0]?.id || ''
  const canConfigure = Boolean(selectedHostId)
  const promote = useMutation({
    mutationFn: async () =>
      call('/projects/{projectId}/environments/{kind}/promotions', () =>
        api.POST('/projects/{projectId}/environments/{kind}/promotions', {
          params: { path: { projectId, kind: 'production' } },
        }),
      ),
    onSuccess: () => {
      setConfirmOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'environments'] })
    },
  })
  const rollback = useMutation({
    mutationFn: async () => {
      const releaseId = productionRelease?.id
      if (!releaseId) return Promise.reject()
      return call('/projects/{projectId}/releases/{releaseId}/rollbacks', () =>
        api.POST('/projects/{projectId}/releases/{releaseId}/rollbacks', {
          params: { path: { projectId, releaseId } },
        }),
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'environments'] })
    },
  })

  const name = project.data?.name ?? t('nav.project.loading')

  if (envs.error) {
    return (
      <ModulePage testid="project-module-environments">
        <ModuleError message={envs.error.message} />
      </ModulePage>
    )
  }

  return (
    <ModulePage testid="project-module-environments">
      <ModuleHeader
        title={moduleTitle(name, t('nav.ptab.env'))}
        subtitle={<PJ id="pjshell.env.subtitle" />}
        actions={
          <div className="flex flex-wrap items-center gap-npc-gap-sm">
            <button
              type="button"
              className={luluButtonClass}
              onClick={() => {
                configure.reset()
                setDomain(project.data?.public_url?.replace(/^https?:\/\//i, '') ?? '')
                setHostId(assignedHosts.find((host) => host.environment_kind === environmentKind)?.id ?? assignedHosts[0]?.id ?? '')
                setConfigureOpen(true)
              }}
            >
              <PJ id="pjshell.env.configure" />
            </button>
            <button
              type="button"
              className={luluPrimaryButtonClass}
              disabled={!promoteReady || promote.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              <PJ id="pjshell.env.promote" />
            </button>
          </div>
        }
      />

      <GroupLabel>
        <PJ id="pjshell.env.group" />
      </GroupLabel>
      <div className="grid grid-cols-1 gap-npc-gap-sm lg:grid-cols-2">
        <ModulePanel
          title={<PJ id="pjshell.env.production.title" />}
          testid="pjshell-env-production"
          badge={envs.isPending ? null : <EnvStatus env={production} />}
        >
          {envs.isPending ? (
            <ModuleSkeleton />
          ) : (
            <EnvFacts
              env={production}
              publicUrl={project.data?.public_url}
              extra={
                canRollback ? (
                  <button
                    type="button"
                    className={luluButtonClass}
                    disabled={rollback.isPending}
                    onClick={() => rollback.mutate()}
                  >
                    <PJ id="pjshell.env.rollback" />
                  </button>
                ) : null
              }
            />
          )}
          {rollback.error ? <p className="mb-0 mt-npc-gap-sm text-npc-caption text-npc-lulu-bad">{rollback.error.message}</p> : null}
        </ModulePanel>
        <ModulePanel
          title={<PJ id="pjshell.env.test.title" />}
          testid="pjshell-env-test"
          badge={envs.isPending ? null : <EnvStatus env={test} />}
        >
          {envs.isPending ? <ModuleSkeleton /> : <EnvFacts env={test} publicUrl={testPublicUrl} />}
          {promoteReady ? null : (
            <p className="mb-0 mt-npc-gap-sm text-npc-meta text-npc-lulu-t4">
              <PJ id="pjshell.env.no_bundle" />
            </p>
          )}
          <p className="mb-0 mt-npc-gap-xs text-npc-meta text-npc-lulu-t4">
            <PJ id="pjshell.env.promote.note" />
          </p>
        </ModulePanel>
      </div>

      <div className="mt-npc-gap-sm">
        <DomainTable
          publicUrl={project.data?.public_url}
          resources={resources.data}
          configuration={configuration ?? persistedConfiguration}
        />
      </div>

      <Modal
        open={configureOpen}
        onOpenChange={setConfigureOpen}
        title={<PJ id="pjshell.env.configure.title" />}
        description={<PJ id="pjshell.env.configure.body" />}
        className="border border-npc-lulu-line-3 bg-npc-lulu-bg-1 text-npc-lulu-t1"
        footer={
          <>
            <button type="button" className={luluButtonClass} onClick={() => setConfigureOpen(false)}>
              <T id="ui.close" />
            </button>
            <button
              type="submit"
              form="environment-configuration-form"
              className={luluPrimaryButtonClass}
              disabled={!canConfigure || configure.isPending}
            >
              <PJ id="pjshell.env.configure.submit" />
            </button>
          </>
        }
      >
        <form
          id="environment-configuration-form"
          className="flex flex-col gap-npc-gap-sm"
          onSubmit={(event) => {
            event.preventDefault()
            if (!canConfigure) return
            configure.mutate(
              { domain, environmentKind, hostId: selectedHostId, files: certificateFiles },
              { onSuccess: (result) => setConfiguration(result) },
            )
          }}
        >
          <EnvironmentField label={<PJ id="pjshell.env.configure.domain_optional" />}>
            <input
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
              placeholder="www.example.com"
              className={environmentFieldClass}
              autoComplete="off"
            />
          </EnvironmentField>
          <EnvironmentField label={<PJ id="pjshell.env.configure.target" />}>
            <select
              value={environmentKind}
              onChange={(event) => {
                const next = event.target.value as EnvKind
                setEnvironmentKind(next)
                setHostId(assignedHosts.find((host) => host.environment_kind === next)?.id ?? assignedHosts[0]?.id ?? '')
              }}
              className={environmentFieldClass}
            >
              <option value="test">{t('pjshell.env.test.title')}</option>
              <option value="production">{t('pjshell.env.production.title')}</option>
            </select>
          </EnvironmentField>
          <EnvironmentField label={<PJ id="pjshell.env.configure.machine" />}>
            <select value={selectedHostId} onChange={(event) => setHostId(event.target.value)} className={environmentFieldClass}>
              <option value="">—</option>
              {assignedHosts.map((host) => (
                <option key={host.id} value={host.id}>{host.name}</option>
              ))}
            </select>
          </EnvironmentField>
          <EnvironmentField label={<PJ id="pjshell.env.configure.files_optional" />}>
            <div className="flex flex-wrap gap-npc-gap-sm">
              <label className={`${luluButtonClass} w-fit cursor-pointer`}>
                <PJ id="pjshell.env.configure.pick" />
                <input
                  type="file"
                  multiple
                  accept=".zip,.pem,.crt,.cer,.key"
                  className="sr-only"
                  onChange={(event) => setCertificateFiles(certificateUploadFiles(event.target.files ?? []))}
                />
              </label>
              <label className={`${luluButtonClass} w-fit cursor-pointer`}>
                <PJ id="pjshell.env.configure.pick_folder" />
                <input
                  type="file"
                  multiple
                  className="sr-only"
                  {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                  onChange={(event) => setCertificateFiles(certificateUploadFiles(event.target.files ?? []))}
                />
              </label>
            </div>
            <p className="mb-0 mt-npc-gap-xs text-npc-meta text-npc-lulu-t4">
              {certificateFiles.length > 0
                ? certificateFiles.map((file) => file.name).join(' · ')
                : <PJ id="pjshell.env.configure.files_hint" />}
            </p>
          </EnvironmentField>
          {configure.error ? <ModuleError message={configure.error.message} /> : null}
          {configuration ? (
            <p className="m-0 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-2 px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-lulu-t2">
              <PJ id={
                configuration.environment_kind === 'test'
                  ? configuration.deployable ? 'pjshell.env.configure.test_ready' : 'pjshell.env.configure.test_done'
                  : configuration.deployable ? 'pjshell.env.configure.ready' : 'pjshell.env.configure.done'
              } />
            </p>
          ) : null}
        </form>
      </Modal>

      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={<PJ id="pjshell.env.promote.title" />}
        description={<PJ id="pjshell.env.promote.body" />}
        className="border border-npc-lulu-line-3 bg-npc-lulu-bg-1 text-npc-lulu-t1"
        footer={
          <>
            <button type="button" className={luluButtonClass} onClick={() => setConfirmOpen(false)}>
              <T id="ui.close" />
            </button>
            <button
              type="button"
              className={luluPrimaryButtonClass}
              disabled={promote.isPending}
              onClick={() => promote.mutate()}
            >
              <PJ id="pjshell.env.promote" />
            </button>
          </>
        }
      >
        <p className="m-0 text-npc-caption text-npc-lulu-t3">
          <PJ id="pjshell.env.promote.note" />
        </p>
      </Modal>
    </ModulePage>
  )
}

function EnvStatus({ env }: { env: Schemas['ProjectEnvironment'] | null }) {
  const copyId = environmentStatusCopy(env)
  if (copyId === 'unverified') return <Unverified />
  const tone = env?.status === 'failed' ? 'bad' : env?.status === 'rolled_back' ? 'warn' : 'neutral'
  return (
    <StatusPill tone={tone}>
      <PJ id={copyId} />
    </StatusPill>
  )
}

function EnvFacts({
  env,
  publicUrl,
  extra,
}: {
  env: Schemas['ProjectEnvironment'] | null
  publicUrl?: string | null
  extra?: ReactNode
}) {
  const release = env?.active_release
  const href = publicHref(publicUrl)
  return (
    <div>
      <KvRows
        rows={[
          {
            key: <PJ id="pjshell.env.address" />,
            value: href ? <span className="font-npc-mono">{publicUrl}</span> : <Unverified />,
          },
          { key: <PJ id="pjshell.env.version" />, value: versionLabel(release) },
          {
            key: <PJ id="pjshell.env.when" />,
            value: release?.created_at ? (
              <span className="font-npc-mono">{hhmm(release.created_at)}</span>
            ) : (
              <Unverified />
            ),
          },
          { key: <PJ id="pjshell.env.probe" />, value: probeLabel(env) },
          { key: <PJ id="pjshell.env.machine" />, value: hostNamesLabel(env) },
          {
            key: <PJ id="pjshell.env.who" />,
            value: release?.created_by_display?.trim() ? release.created_by_display : <Unverified />,
          },
        ]}
      />
      <div className="mt-npc-gap-sm flex flex-wrap items-center gap-npc-gap-sm">
        {href ? (
          <a href={href} target="_blank" rel="noreferrer" className={luluButtonClass}>
            <PJ id="pjshell.env.open" />
          </a>
        ) : null}
        {extra}
      </div>
    </div>
  )
}

const DOMAIN_GRID = 'minmax(0,1.2fr) 78px minmax(0,1fr) 96px 92px 88px'

function DomainTable({
  publicUrl,
  resources,
  configuration,
}: {
  publicUrl?: string | null
  resources?: Schemas['ProjectResources']
  configuration: Schemas['EnvironmentConfiguration'] | null
}) {
  const href = publicHref(publicUrl)
  const certificateStored = configuration?.certificate_status === 'stored' || resources?.cert_status === 'configured'
  const certificateExpiry = configuration?.certificate_expires_at ?? resources?.cert_expires_at
  return (
    <DataTable
      testid="pjshell-env-domains"
      grid={DOMAIN_GRID}
      hint={<PJ id="pjshell.env.domains" />}
      columns={[
        <PJ key="d" id="pjshell.env.domains" />,
        <PJ key="p" id="pjshell.env.col.point" />,
        <PJ key="r" id="pjshell.env.col.resolve" />,
        <PJ key="c" id="pjshell.env.col.cert" />,
        <PJ key="e" id="pjshell.env.col.expiry" />,
        <PJ key="s" id="pjshell.env.col.state" />,
      ]}
    >
      {href ? (
        <DataRow grid={DOMAIN_GRID}>
          <DataCell mono>{publicUrl}</DataCell>
          <DataCell>
            <PJ id="pjshell.env.production" />
          </DataCell>
          <DataCell>
            {configuration?.dns_status === 'verified' ? (
              <PJ id="pjshell.env.domain.verified" />
            ) : (
              <PJ id={configuration ? 'pjshell.env.domain.waiting' : 'pjshell.env.domain.registered'} />
            )}
          </DataCell>
          <DataCell>
            {certificateStored ? <PJ id="pjshell.env.certificate.stored" /> : <Unverified />}
          </DataCell>
          <DataCell>
            {certificateExpiry ? monthDayTime(certificateExpiry) : <Unverified />}
          </DataCell>
          <DataCell>
            {configuration?.deployable ? <PJ id="pjshell.env.status.ready" /> : <Unverified />}
          </DataCell>
        </DataRow>
      ) : (
        <DataRow grid={DOMAIN_GRID}>
          <DataCell>
            <Unverified />
          </DataCell>
          <DataCell>
            <Unverified />
          </DataCell>
          <DataCell>
            <Unverified />
          </DataCell>
          <DataCell>
            <Unverified />
          </DataCell>
          <DataCell>
            <Unverified />
          </DataCell>
          <DataCell>
            <Unverified />
          </DataCell>
        </DataRow>
      )}
    </DataTable>
  )
}

const environmentFieldClass =
  'h-9 w-full rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-0 px-3 text-npc-body-sm text-npc-lulu-t1 outline-none'

function EnvironmentField({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-npc-gap-xs text-npc-caption font-medium text-npc-lulu-t2">
      <span>{label}</span>
      {children}
    </div>
  )
}

function publicHref(value?: string | null): string | null {
  const trimmed = value?.trim()
  if (!trimmed) return null
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

function machineAddressHref(value?: string | null): string | null {
  const trimmed = value?.trim()
  if (!trimmed) return null
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `http://${trimmed}`
}

function versionLabel(release: Schemas['EnvironmentRelease'] | undefined) {
  if (release?.version_no == null) return <Unverified />
  return <span className="font-npc-mono">{`v${release.version_no}`}</span>
}

function probeLabel(env: Schemas['ProjectEnvironment'] | null) {
  if (!env || env.last_verification_passed == null) return <Unverified />
  return env.last_verification_passed ? (
    <PJ id="pjshell.env.status.ready" />
  ) : (
    <PJ id="pjshell.env.status.failed" />
  )
}

function hostNamesLabel(env: Schemas['ProjectEnvironment'] | null) {
  const names = env?.host_names?.filter((name) => name.trim()) ?? []
  if (names.length === 0) return <Uncollected />
  return names.join(' · ')
}
